import {
  CopyToClipboardButtonComponent,
  type CopyToClipboardButtonComponentProps,
  copyToClipboard,
} from '@ctw/shared/components/copy-to-clipboard';
import { notify } from '@ctw/shared/components/toast';
import { tw } from '@ctw/shared/utils/tailwind';
import { type FunctionComponent, type MouseEvent, useCallback, useMemo } from 'react';

export type RiskGapCopyButtonProps = Omit<CopyToClipboardButtonComponentProps, 'onClick'> & {
  /** The content we will add to the pasteboard. */
  copyContent: string | undefined;
  /** The message we display as a toast once the content is copied to the pasteboard. */
  copySuccessMessage?: string;
  /** Optional: Called when the copy content succeeds. */
  onClick?: CopyToClipboardButtonComponentProps['onClick'];
};

/**
 * A copy button with common styles and behaviors for risk gaps.
 * Used only in the v2 risk interface.
 * Expected to run within a toast context.
 */
export const RiskGapCopyButton: FunctionComponent<RiskGapCopyButtonProps> = ({
  copyContent,
  copySuccessMessage = 'Copied to clipboard',
  onClick,
  buttonDisabled: buttonDisabledProp,
  ...rest
}) => {
  const handleCopy: typeof onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // stop propagation so we don't simultaneously trigger nested cards
      event.stopPropagation();
      if (onClick) {
        // pass the onClick through if necessary
        onClick(event);
      }
      if (!copyContent) {
        return;
      }
      void copyToClipboard(copyContent, () => {
        if (copySuccessMessage) {
          notify({ type: 'info', title: copySuccessMessage });
        }
      });
    },
    [onClick, copyContent, copySuccessMessage],
  );
  const buttonDisabled = useMemo(() => {
    // defer to an explicitly set prop
    if (buttonDisabledProp !== undefined) {
      return buttonDisabledProp;
    }
    // Implicitly disable if there is no content to copy.
    return !copyContent;
  }, [buttonDisabledProp, copyContent]);

  return (
    <CopyToClipboardButtonComponent
      tooltipContent={'Copy'}
      buttonDisabled={buttonDisabled}
      onClick={handleCopy}
      buttonSize={'sm'}
      buttonClassName={tw`shrink h-8 w-8 rounded-md cursor-pointer opacity-0 transition-opacity group-hover:opacity-100`}
      iconClassName={tw`h-4 w-4 text-muted-foreground`}
      {...rest}
    />
  );
};
